import Phaser from 'phaser';
import { COLORS, COLORS_HEX, FONTS } from '@/ui/theme';
import { AudioManager } from '@/utils/AudioManager';
import { KioskWatchdog } from '@/utils/KioskWatchdog';
import { createPillButton } from '@/ui/PillButton';
import { createImageCard } from '@/ui/ImageCard';
import { drawSelectionHalo } from '@/ui/SelectionHalo';
import { GamepadNav } from '@/utils/GamepadNav';
import { templateCardText } from '@/utils/CardText';
import type { NarrativeCard } from '@/utils/NarrativeCards';
import { HEADER_CARD_GAP } from '@/config/constants';

/**
 * NarrativeCardModal — title + body + CTA card used for between-world
 * bridges and the pre-boss intro. Same visual family as
 * EducationIntroModal (deep navy panel, aqua trim, cyan CTA).
 *
 * When `card.image` resolves to a loaded texture, the layout matches
 * EducationIntroModal — 9:16 hero on the left, copy column on the right.
 * When no image is supplied, the title + body + CTA stack centered on the
 * full panel width, used for snappier mid-world bridge cards.
 */
export class NarrativeCardModal extends Phaser.GameObjects.Container {
  private readonly overlay: Phaser.GameObjects.Rectangle;
  private readonly panel: Phaser.GameObjects.Graphics;
  private readonly headerGroup: Phaser.GameObjects.Container;
  private readonly cardGroup: Phaser.GameObjects.Container;
  private readonly ctaButton: Phaser.GameObjects.Container;
  private readonly onDismiss: () => void;
  private dismissed = false;
  private nav?: GamepadNav;
  private padTick?: () => void;

  private static readonly PANEL_W_HERO = 1180;
  private static readonly PANEL_H_HERO = 720;
  private static readonly PANEL_W_TEXT = 1180;
  private static readonly PANEL_H_TEXT = 600;
  private static readonly PANEL_RADIUS = 32;

  constructor(scene: Phaser.Scene, card: NarrativeCard, onDismiss: () => void) {
    super(scene, 0, 0);
    scene.add.existing(this);
    this.setDepth(10_000);
    this.setScrollFactor(0);
    this.onDismiss = onDismiss;

    const cx = 960;
    const hasImage = !!card.image && scene.textures.exists(card.image);
    const pw = hasImage ? NarrativeCardModal.PANEL_W_HERO : NarrativeCardModal.PANEL_W_TEXT;
    const ph = hasImage ? NarrativeCardModal.PANEL_H_HERO : NarrativeCardModal.PANEL_H_TEXT;
    const panelLeft = cx - pw / 2;
    const panelRadius = NarrativeCardModal.PANEL_RADIUS;

    // ── scrim — heavier than the per-stage education modal so the boss
    //   HP bar / score HUD sitting around the modal edge doesn't bleed
    //   through and break the cinematic read of the bridge / boss card.
    this.overlay = scene.add.rectangle(0, 0, 1920, 1080, 0x020b18, 0.78)
      .setOrigin(0, 0)
      .setInteractive();
    this.add(this.overlay);

    // ── header: HELMEPA + smaller POSEIDON CHALLENGE banner ────────────
    this.headerGroup = scene.add.container(0, 0);
    this.add(this.headerGroup);
    if (scene.textures.exists('helmepa_logo')) {
      const helmepa = scene.add.image(cx, 88, 'helmepa_logo').setOrigin(0.5);
      helmepa.setScale(162 / helmepa.width);
      helmepa.setAlpha(0.95);
      this.headerGroup.add(helmepa);
    }
    let titleBottom = 268.6; // fallback if title texture is missing
    if (scene.textures.exists('title_logo_v4')) {
      const title = scene.add.image(cx, 200, 'title_logo_v4').setOrigin(0.5);
      title.setScale(378 / title.width);
      this.headerGroup.add(title);
      titleBottom = title.y + title.displayHeight / 2;
    }

    // ── main card panel ────────────────────────────────────────────────
    const panelTop = Math.round(titleBottom + HEADER_CARD_GAP);
    this.panel = scene.add.graphics();
    this.drawPanel(this.panel, panelLeft, panelTop, pw, ph, panelRadius);
    this.add(this.panel);

    this.cardGroup = scene.add.container(0, 0);
    this.add(this.cardGroup);

    const ctaY = panelTop + ph - 95;

    if (hasImage) {
      // ── 9:16 hero on the left, title + body on the right.
      //   Image lives in a self-masking ImageCard, so it can never bleed
      //   past its rounded frame. Bottom aligns with the CTA button so
      //   the image and button visually end on the same line.
      const imgInsetTop = 40;
      const imgInsetX = 40;
      const ctaHalfH = 55;
      const imgY = panelTop + imgInsetTop;
      const imgH = (ctaY + ctaHalfH) - imgY;
      // Size the frame to the image's own aspect ratio so the whole
      // illustration fills it edge to edge, with no crop and no letterbox bands.
      const heroSrc = scene.textures.get(card.image!).getSourceImage() as HTMLImageElement;
      const imgW = Math.round(imgH * heroSrc.width / heroSrc.height);
      const imgX = panelLeft + imgInsetX;
      const copyPadX = 70;
      const copyGap = 60;

      this.cardGroup.add(createImageCard(scene, {
        x: imgX, y: imgY, w: imgW, h: imgH,
        textureKey: card.image!,
      }));

      const textX = imgX + imgW + copyGap;
      const textRight = panelLeft + pw - copyPadX;
      const textW = textRight - textX;

      // Title + body fonts dropped ~10% for breathing room; vertical block
      // is centered between the panel top and the CTA so copy never hugs
      // the modal edge.
      const titleStyle: Phaser.Types.GameObjects.Text.TextStyle = {
        fontFamily: FONTS.display,
        fontSize: '27px',
        color: COLORS_HEX.aquaLight,
        stroke: COLORS_HEX.midnightDepth,
        strokeThickness: 3,
        wordWrap: { width: textW, useAdvancedWrap: true },
        lineSpacing: 4,
        shadow: { offsetX: 0, offsetY: 2, color: '#000000', blur: 6, fill: true },
      };
      const bodyStyle: Phaser.Types.GameObjects.Text.TextStyle = {
        fontFamily: FONTS.displayLight,
        fontSize: '22px',
        color: COLORS_HEX.textPrimary,
        wordWrap: { width: textW, useAdvancedWrap: true },
        lineSpacing: 6,
        shadow: { offsetX: 0, offsetY: 2, color: COLORS_HEX.midnightDepth, blur: 4, fill: true },
      };

      const titleText = scene.add.text(textX, 0, card.title, titleStyle).setOrigin(0, 0);
      const bodyText = scene.add.text(textX, 0, templateCardText(scene, card.body), bodyStyle).setOrigin(0, 0);

      const titleGap = 48;
      const blockH = titleText.height + titleGap + bodyText.height;
      const ctaTop = ctaY - 48;
      const availableTop = panelTop + 40;
      const availableH = ctaTop - availableTop;
      const blockTop = availableTop + Math.max((availableH - blockH) / 2, 20);
      titleText.setY(blockTop);
      bodyText.setY(blockTop + titleText.height + titleGap);
      this.cardGroup.add(titleText);
      this.cardGroup.add(bodyText);

      const ctaCx = (textX + textRight) / 2;
      this.ctaButton = createPillButton(scene, {
        x: ctaCx,
        y: ctaY,
        label: card.cta,
        variant: 'primary',
        fontSize: 34,
        onClick: () => this.dismiss(),
      });
      this.add(this.ctaButton);
    } else {
      // ── text-only centered layout for snappy bridge cards
      const padX = 100;
      const textW = pw - padX * 2;

      // Yellow Skranji headline, matching EducationIntroModal so the
      // between-mission "Next mission" bridge reads as the same card family.
      const titleStyle: Phaser.Types.GameObjects.Text.TextStyle = {
        fontFamily: FONTS.fancyRegular,
        fontSize: '56px',
        color: '#ffe000',
        stroke: '#000000',
        strokeThickness: 7,
        align: 'center',
        wordWrap: { width: textW, useAdvancedWrap: true },
        lineSpacing: 2,
        shadow: { offsetX: 0, offsetY: 4, color: '#000000', blur: 10, fill: true },
      };
      const bodyStyle: Phaser.Types.GameObjects.Text.TextStyle = {
        fontFamily: FONTS.displayLight,
        fontSize: '30px',
        color: COLORS_HEX.textPrimary,
        align: 'center',
        wordWrap: { width: textW, useAdvancedWrap: true },
        lineSpacing: 12,
        shadow: { offsetX: 0, offsetY: 2, color: COLORS_HEX.midnightDepth, blur: 4, fill: true },
      };

      const titleText = scene.add.text(cx, 0, card.title, titleStyle).setOrigin(0.5, 0);
      const bodyText = scene.add.text(cx, 0, templateCardText(scene, card.body), bodyStyle).setOrigin(0.5, 0);

      const titleGap = 58;
      const blockH = titleText.height + titleGap + bodyText.height;
      const ctaTop = ctaY - 48;
      const availableTop = panelTop + 60;
      const availableH = ctaTop - availableTop;
      const blockTop = availableTop + Math.max((availableH - blockH) / 2, 20);
      titleText.setY(blockTop);
      bodyText.setY(blockTop + titleText.height + titleGap);
      this.cardGroup.add(titleText);
      this.cardGroup.add(bodyText);

      this.ctaButton = createPillButton(scene, {
        x: cx,
        y: ctaY,
        label: card.cta,
        variant: 'primary',
        fontSize: 34,
        onClick: () => this.dismiss(),
      });
      this.add(this.ctaButton);
    }

    this.setAlpha(0);
    (this.panel as any).setScale?.(0.96);

    this.nav = new GamepadNav(scene, {
      onConfirm: () => this.dismiss(),
      onCancel:  () => this.dismiss(),
      onPause:   () => this.dismiss(),
    });

    // Selection halo on the lone CTA — visible whenever a gamepad is
    // connected, since any face button dismisses the card and the joystick
    // is effectively "on" the only target. Hidden for mouse / keyboard.
    const haloGraphics = scene.add.graphics();
    this.add(haloGraphics);
    this.bringToTop(this.ctaButton);
    this.padTick = () => {
      this.nav?.update();
      if (this.nav?.isPadConnected()) {
        drawSelectionHalo(haloGraphics, {
          cx: this.ctaButton.x,
          cy: this.ctaButton.y,
          w: ((this.ctaButton as any).pillWidth ?? 0) * this.ctaButton.scaleX,
          h: ((this.ctaButton as any).pillHeight ?? 0) * this.ctaButton.scaleY,
          rx: ((this.ctaButton as any).pillCapRx ?? 0) * this.ctaButton.scaleX,
          ry: ((this.ctaButton as any).pillCapRy ?? 0) * this.ctaButton.scaleY,
        }, { gap: 14 });
      } else {
        haloGraphics.clear();
      }
    };
    scene.events.on(Phaser.Scenes.Events.UPDATE, this.padTick);
  }

  show(): void {
    // Mark a blocking dialog open for the kiosk idle watchdog; release it when
    // this object is destroyed (covers both normal dismiss and scene shutdown,
    // exactly once, so the counter can't leak).
    KioskWatchdog.instance.pushModal();
    this.once(Phaser.GameObjects.Events.DESTROY, () => KioskWatchdog.instance.popModal());
    this.scene.tweens.add({ targets: this, alpha: 1, duration: 280, ease: 'Power2' });
    this.scene.tweens.add({
      targets: [this.panel],
      scaleX: 1, scaleY: 1,
      duration: 360, ease: 'Back.easeOut',
    });
    AudioManager.getInstance().playSFX(this.scene, 'sfx_whoosh_reveal', { volume: 0.45 });
  }

  private drawPanel(g: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number, r: number): void {
    // Matches StageClearScene / GameOverScene: translucent navy fill
    // + single aqua hairline. One modal chrome across the whole game.
    g.fillStyle(COLORS.deepOcean, 0.55);
    g.fillRoundedRect(x, y, w, h, r);
    g.lineStyle(2, COLORS.aqua, 0.70);
    g.strokeRoundedRect(x, y, w, h, r);
  }

  private dismiss(): void {
    if (this.dismissed) return;
    this.dismissed = true;
    if (this.padTick) {
      this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.padTick);
      this.padTick = undefined;
    }
    this.nav?.destroy();
    this.nav = undefined;
    AudioManager.getInstance().playSFX(this.scene, 'sfx_iris_open', { volume: 0.5 });
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      duration: 220,
      ease: 'Power2',
      onComplete: () => {
        this.onDismiss();
        this.destroy();
      },
    });
  }
}
